/**
 * Cloudinary Utility Functions
 * Handles image uploads and transformations
 */

// @ts-ignore - Vite env variables
const CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME
// @ts-ignore - Vite env variables
const UPLOAD_PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET
const CLOUDINARY_URL = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`

/**
 * Upload image to Cloudinary
 * @param file - Image file to upload
 * @param folder - Cloudinary folder path (e.g., 'scoutang/players')
 * @returns Promise with upload response
 */
export async function uploadToCloudinary(
  file: File,
  folder: string = 'scoutang'
): Promise<CloudinaryResponse> {
  // Validate file
  if (!file) {
    throw new Error('No file provided')
  }

  // Validate file size (5MB max)
  const MAX_FILE_SIZE = 5242880 // 5MB
  if (file.size > MAX_FILE_SIZE) {
    throw new Error('File size exceeds 5MB limit')
  }

  // Validate file type
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Only JPEG, PNG, WebP, and GIF images are allowed')
  }

  try {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('upload_preset', UPLOAD_PRESET)
    formData.append('folder', folder)
    formData.append('resource_type', 'auto')

    const response = await fetch(CLOUDINARY_URL, {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`)
    }

    const data: CloudinaryResponse = await response.json()
    return data
  } catch (error) {
    console.error('Cloudinary upload error:', error)
    throw error
  }
}

/**
 * Get optimized image URL with transformations
 * @param publicId - Cloudinary public ID
 * @param options - Transformation options
 * @returns Optimized image URL
 */
export function getCloudinaryUrl(
  publicId: string,
  options: CloudinaryOptions = {}
): string {
  const {
    width = 500,
    height = 500,
    crop = 'fill',
    gravity = 'auto',
    quality = 'auto',
    format = 'webp',
  } = options

  return `https://res.cloudinary.com/${CLOUD_NAME}/image/fetch/w_${width},h_${height},c_${crop},g_${gravity},q_${quality},f_${format}/${publicId}`
}

/**
 * Get thumbnail URL
 * @param publicId - Cloudinary public ID
 * @returns Small thumbnail URL
 */
export function getThumbnailUrl(publicId: string): string {
  return getCloudinaryUrl(publicId, {
    width: 150,
    height: 150,
    crop: 'fill',
    quality: 'auto',
  })
}

/**
 * Get display image URL (optimized for web)
 * @param publicId - Cloudinary public ID
 * @returns Optimized display image URL
 */
export function getDisplayUrl(publicId: string): string {
  return getCloudinaryUrl(publicId, {
    width: 600,
    height: 600,
    crop: 'fill',
    quality: 'auto',
    format: 'webp',
  })
}

/**
 * Delete image from Cloudinary
 * @param publicId - Cloudinary public ID
 * @returns Promise with deletion response
 */
export async function deleteFromCloudinary(publicId: string): Promise<void> {
  try {
    // @ts-ignore - Vite env variables
    const api_key = import.meta.env.VITE_CLOUDINARY_API_KEY
    if (!api_key) {
      console.warn('Cloudinary API key not configured, skipping deletion')
      return
    }

    const formData = new FormData()
    formData.append('public_id', publicId)
    formData.append('api_key', api_key)

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/destroy`,
      {
        method: 'POST',
        body: formData,
      }
    )

    if (!response.ok) {
      throw new Error(`Deletion failed with status ${response.status}`)
    }
  } catch (error) {
    console.error('Cloudinary deletion error:', error)
    throw error
  }
}

export interface CloudinaryResponse {
  public_id: string
  version: number
  signature: string
  width: number
  height: number
  format: string
  resource_type: string
  created_at: string
  tags: string[]
  bytes: number
  type: string
  etag: string
  placeholder: boolean
  url: string
  secure_url: string
  folder: string
  original_filename: string
}

export interface CloudinaryOptions {
  width?: number
  height?: number
  crop?: string
  gravity?: string
  quality?: string
  format?: string
  radius?: number
  angle?: number
  overlay?: string
  underlay?: string
  effect?: string
  flags?: string
}

export default {
  uploadToCloudinary,
  getCloudinaryUrl,
  getThumbnailUrl,
  getDisplayUrl,
  deleteFromCloudinary,
}
